import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import EstimateDocument from './EstimateDocument';
import { EstimateData, fetchEstimate } from '../../services/estimateService';
import { useUserSession } from '../Auth/userSession';
import { fetchMyOrderDetail } from '../MyPage/myOrdersService';
import { getProofToken, saveProofToken } from '../MyPage/proofAccess';
import styles from './Estimate.module.scss';

interface EstimateViewProps {
  orderId: string;
  token: string | null;
  backTo: string;
  backLabel: string;
}

// 견적서 화면 공통 부분: 불러오기 + 인쇄/PDF 저장 버튼
export function EstimateView({ orderId, token, backTo, backLabel }: EstimateViewProps) {
  const [data, setData] = useState<EstimateData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setError(null);
    fetchEstimate(orderId, token)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : '견적서를 불러오지 못했습니다.'));
  }, [orderId, token]);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Link to={backTo} className={styles.btnLine}>
          ← {backLabel}
        </Link>
        {data && (
          <button type="button" className={styles.btnDark} onClick={() => window.print()}>
            PDF 저장 · 인쇄
          </button>
        )}
      </div>
      {data && <p className={styles.printHint}>인쇄 창에서 대상(프린터)을 &lsquo;PDF로 저장&rsquo;으로 선택하면 파일로 받을 수 있습니다.</p>}

      {error && <p className={styles.error}>{error}</p>}
      {!error && !data && token && <p className={styles.loading}>견적서를 불러오는 중...</p>}
      {data && <EstimateDocument data={data} />}
    </div>
  );
}

// 고객용 견적서 (/estimate/:id): 비회원 조회 또는 회원 로그인으로 받은 주문 접근 토큰이 필요하다
function Estimate() {
  const { id } = useParams<{ id: string }>();
  const [session] = useUserSession();
  const [token, setToken] = useState<string | null>(() => (id ? getProofToken(id) : null));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || token || !session) return;
    fetchMyOrderDetail(id, session.token)
      .then((order) => {
        if (!order.proofToken) return;
        saveProofToken(id, order.proofToken);
        setToken(order.proofToken);
      })
      .catch((err) => setError(err instanceof Error ? err.message : '본인 확인 중 오류가 발생했습니다.'));
  }, [id, token, session]);

  if (!id) return null;

  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.gate}>
          <h1>견적서 다운로드</h1>
          {error ? (
            <p className={styles.error}>{error}</p>
          ) : session ? (
            <p>본인 확인 중...</p>
          ) : (
            <>
              <p>견적서는 주문하신 분만 볼 수 있습니다. 주문조회(비회원) 또는 로그인(회원) 후 다시 열어주세요.</p>
              <div className={styles.gateActions}>
                <Link to="/lookup" className={styles.btnDark}>
                  비회원 주문조회
                </Link>
                <Link to="/login" state={{ from: `/estimate/${id}` }} className={styles.btnLine}>
                  로그인
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return <EstimateView orderId={id} token={token} backTo={session ? '/mypage' : '/lookup'} backLabel="주문 정보로" />;
}

export default Estimate;
